// Per-buying-guide equipment illustrations used on the guides listing card
// header and the buying-guide page hero. Decorative line-art SVGs, monochrome,
// tinted by the consumer via the `color` prop — same contract as
// `grass-blades.tsx`. viewBox: 0 0 200 120 — wide aspect that sits in the
// tinted card header.

import type { ComponentType } from "react"

// Shared contract for every guide illustration: monochrome line-art, sized to
// fill its container, tinted by the consumer via `color`. Care-task and
// buying-guide illustrations both import this so stroke weights and the ground
// treatment stay consistent across surfaces.
export interface IllustrationProps {
  color?: string
  className?: string
}

export const Frame = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <svg
    viewBox="0 0 200 120"
    width="100%"
    height="100%"
    preserveAspectRatio="xMidYMax meet"
    aria-hidden="true"
    className={className}
  >
    {children}
  </svg>
)

export const Baseline = ({ color }: { color: string }) => (
  <line x1="18" y1="111" x2="182" y2="111" stroke={color} strokeWidth="1" opacity="0.25" strokeLinecap="round" />
)

// A few low grass tufts so the subject reads as "on a lawn", not floating.
export const GroundTufts = ({ color }: { color: string }) => (
  <g fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.4">
    <path d="M26 111 C 25 105, 28 103, 27 99" />
    <path d="M31 111 C 32 106, 29 104, 31 100" />
    <path d="M172 111 C 171 105, 174 103, 173 99" />
    <path d="M177 111 C 178 106, 175 104, 177 100" />
  </g>
)

// Robot mower: low rounded dome on two wheels, a charging dock to the right,
// and a dotted boundary wire along the ground.
const RobotMower: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Boundary wire — the defining robot-mower install detail */}
    <line
      x1="20"
      y1="108"
      x2="150"
      y2="108"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeDasharray="2 5"
      opacity="0.5"
    />

    {/* Charging dock */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M150 106 L 176 106" strokeWidth="2.4" opacity="0.85" />
      <path d="M156 106 L 156 86 L 170 86 L 170 106" strokeWidth="2.2" />
      <path d="M159 92 L 167 92" strokeWidth="1.8" opacity="0.7" />
      <path d="M159 98 L 167 98" strokeWidth="1.8" opacity="0.7" />
    </g>

    {/* Robot body — low dome */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path
        d="M50 100 L 50 80 C 50 62, 66 52, 90 52 C 114 52, 124 62, 124 80 L 124 100 Z"
        strokeWidth="2.6"
      />
      {/* Top control button + seam */}
      <circle cx="87" cy="64" r="4.5" strokeWidth="2.2" />
      <path d="M64 78 L 110 78" strokeWidth="1.6" opacity="0.5" />
      {/* Front bumper sensor */}
      <path d="M50 86 L 44 86 C 41 86, 40 88, 40 91 L 40 95 C 40 98, 42 100, 45 100 L 50 100" strokeWidth="2.2" />
    </g>

    {/* Cutting disc peeking under the deck */}
    <g fill="none" stroke={color} strokeLinecap="round" opacity="0.65">
      <path d="M68 101 L 106 101" strokeWidth="1.6" />
      <path d="M74 101 L 72 105" strokeWidth="1.4" />
      <path d="M90 101 L 90 106" strokeWidth="1.4" />
      <path d="M100 101 L 102 105" strokeWidth="1.4" />
    </g>

    {/* Wheels */}
    <g fill="none" stroke={color} strokeWidth="2.4">
      <circle cx="64" cy="101" r="7" />
      <circle cx="110" cy="101" r="7" />
    </g>
    <g fill={color} stroke="none" opacity="0.85">
      <circle cx="64" cy="101" r="2" />
      <circle cx="110" cy="101" r="2" />
    </g>
  </Frame>
)

// Riding mower / lawn tractor in side profile, facing right: big rear drive
// wheel, small front wheel, hood over the nose, seat, and a circular steering
// wheel on an angled column. Drawn for legibility at card-thumbnail size — a
// circle reads as a wheel where a bare angled line reads as a letter.
const RidingMower: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Mower deck — shallow cutting housing slung under the chassis between
        the wheels */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M76 86 L 76 96 C 76 99, 78 101, 81 101 L 124 101 C 127 101, 129 99, 129 96 L 129 86" strokeWidth="2.2" opacity="0.85" />
    </g>

    {/* Chassis frame beam */}
    <line x1="44" y1="86" x2="132" y2="86" stroke={color} strokeWidth="2.6" strokeLinecap="round" />

    {/* Hood / engine over the nose */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.6">
      <path d="M128 86 L 128 66 C 128 61, 131 58, 136 58 L 166 58 C 171 58, 174 61, 174 66 L 174 86" />
      {/* Grille slats */}
      <path d="M166 66 L 166 78" strokeWidth="1.6" opacity="0.55" />
      <path d="M170 67 L 170 77" strokeWidth="1.6" opacity="0.5" />
    </g>

    {/* Seat — pan + backrest */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.6">
      <path d="M88 73 L 112 73" />
      <path d="M88 73 L 86 55 C 86 51, 88 50, 92 50 L 96 50" />
      <line x1="98" y1="73" x2="98" y2="86" strokeWidth="2.2" />
    </g>

    {/* Steering — angled column with a circular wheel */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M124 66 L 116 52" strokeWidth="2.4" />
      <circle cx="113" cy="49" r="5" strokeWidth="2.2" />
    </g>

    {/* Rear drive wheel (large) */}
    <g fill="none" stroke={color} strokeWidth="2.6">
      <circle cx="60" cy="90" r="18" />
      <circle cx="60" cy="90" r="6.5" strokeWidth="2.2" />
    </g>
    <g stroke={color} strokeWidth="1.4" opacity="0.55" strokeLinecap="round">
      <path d="M60 72 L 60 78" />
      <path d="M60 102 L 60 108" />
      <path d="M42 90 L 48 90" />
      <path d="M72 90 L 78 90" />
    </g>

    {/* Front wheel (small) */}
    <g fill="none" stroke={color} strokeWidth="2.4">
      <circle cx="150" cy="98" r="11" />
      <circle cx="150" cy="98" r="3.4" strokeWidth="2" />
    </g>
  </Frame>
)

// Liquid fertilizer: a hose-end sprayer bottle in front view — the Simple Lawn
// Solutions form factor this guide centers on. Bottle body with a screw-on
// sprayer head (setting dial + angled nozzle), a droplet emblem and label lines
// on the body, and a little spray mist off the nozzle so it reads as "applying",
// not just "a bottle".
const LiquidFertilizer: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Spray mist off the nozzle tip */}
    <g fill={color} stroke="none" opacity="0.7">
      <circle cx="141" cy="17" r="1.5" />
      <circle cx="147" cy="21" r="1.2" />
      <circle cx="138" cy="24" r="1.2" />
    </g>
    <g fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.55">
      <path d="M135 20 L 140 15" />
      <path d="M137 25 L 143 23" />
    </g>

    {/* Angled spray nozzle */}
    <path d="M114 37 L 133 22" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />

    {/* Sprayer head block + setting dial */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M86 43 L 86 34 C 86 31, 88 29, 91 29 L 110 29 C 113 29, 115 31, 115 34 L 115 43 Z" strokeWidth="2.2" />
      <circle cx="94" cy="27" r="4" strokeWidth="2" />
    </g>

    {/* Cap / collar */}
    <path
      d="M90 50 L 90 45 C 90 44, 91 43, 92 43 L 108 43 C 109 43, 110 44, 110 45 L 110 50"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Bottle body */}
    <path
      d="M78 104 L 78 72 C 78 64, 84 60, 92 58 L 92 50 L 108 50 L 108 58 C 116 60, 122 64, 122 72 L 122 104 C 122 107, 120 108, 117 108 L 83 108 C 80 108, 78 107, 78 104 Z"
      fill="none"
      stroke={color}
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Droplet emblem on the label */}
    <path
      d="M100 70 C 104 75, 105 78, 100 80 C 95 78, 96 75, 100 70 Z"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.7"
    />

    {/* Label lines */}
    <g stroke={color} strokeLinecap="round" opacity="0.45">
      <path d="M89 88 L 111 88" strokeWidth="1.8" />
      <path d="M92 94 L 108 94" strokeWidth="1.6" />
    </g>
  </Frame>
)

export const BUYING_GUIDE_ILLUSTRATIONS: Record<string, ComponentType<IllustrationProps>> = {
  "robot-lawn-mower-buying-guide": RobotMower,
  "riding-mower-buying-guide": RidingMower,
  "best-liquid-lawn-fertilizer": LiquidFertilizer,
}
